import { useState } from 'react';
import { FileText, Download, Calendar, BarChart3, TrendingUp, Package, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { apiFetch } from '../utils/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function ReportsPage() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const reportTypes = [
    {
      id: 'sales',
      name: '売上レポート',
      description: '期間内の売上集計、店舗別・商品別分析',
      icon: TrendingUp,
      color: 'bg-green-500',
    },
    {
      id: 'orders',
      name: '受注レポート',
      description: '受注件数、ステータス別推移、平均単価',
      icon: BarChart3,
      color: 'bg-blue-500',
    },
    {
      id: 'inventory',
      name: '在庫レポート',
      description: '在庫推移、回転率、不足アラート履歴',
      icon: Package,
      color: 'bg-orange-500',
    },
    {
      id: 'sync',
      name: '同期レポート',
      description: '同期成功率、エラー履歴、処理時間',
      icon: FileText,
      color: 'bg-purple-500',
    },
  ];

  async function downloadReport(reportId: string, format: 'csv' | 'pdf') {
    setLoading(`${reportId}-${format}`);
    setError('');

    try {
      let csvContent = '';
      let filename = '';

      switch (reportId) {
        case 'orders':
        case 'sales': {
          const params = new URLSearchParams({
            limit: '1000',
            startDate,
            endDate,
          });
          const response = await apiFetch(`/api/orders?${params}`);
          const data = await response.json();

          if (!response.ok) throw new Error(data.error);

          if (reportId === 'orders') {
            const headers = ['注文番号', '店舗', '顧客名', '金額', 'ステータス', '注文日時'];
            const rows = (data.orders || []).map((order: any) => [
              order.mallOrderId,
              order.shop?.shopName || '',
              order.customerInfo?.name || '',
              order.totalAmount,
              getStatusLabel(order.orderStatus),
              new Date(order.orderedAt).toLocaleString('ja-JP'),
            ]);
            csvContent = [
              headers.join(','),
              ...rows.map((row: any[]) => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            ].join('\n');
            filename = `orders_${startDate}_${endDate}.csv`;
          } else {
            // 売上レポート
            const orders = data.orders || [];
            const totalSales = orders.reduce((sum: number, o: any) => sum + Number(o.totalAmount || 0), 0);
            const avgOrderValue = orders.length > 0 ? totalSales / orders.length : 0;

            // 店舗別集計
            const shopSales: Record<string, { count: number; total: number }> = {};
            orders.forEach((o: any) => {
              const shopName = o.shop?.shopName || '不明';
              if (!shopSales[shopName]) shopSales[shopName] = { count: 0, total: 0 };
              shopSales[shopName].count++;
              shopSales[shopName].total += Number(o.totalAmount || 0);
            });

            const headers = ['店舗名', '受注件数', '売上合計', '平均単価'];
            const rows = Object.entries(shopSales).map(([name, stats]) => [
              name,
              stats.count,
              stats.total,
              Math.round(stats.total / stats.count),
            ]);
            rows.push(['--- 合計 ---', orders.length, totalSales, Math.round(avgOrderValue)]);

            csvContent = [
              `# 売上レポート (${startDate} 〜 ${endDate})`,
              '',
              headers.join(','),
              ...rows.map((row: any[]) => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            ].join('\n');
            filename = `sales_${startDate}_${endDate}.csv`;
          }
          break;
        }

        case 'inventory': {
          const response = await apiFetch('/api/inventory?limit=1000');
          const data = await response.json();

          if (!response.ok) throw new Error(data.error);

          const headers = ['商品名', 'SKUコード', 'バリアントコード', '総在庫', '引当済', '販売可能', '安全在庫', '同期'];
          const rows = (data.inventory || []).map((inv: any) => [
            inv.product?.productName || '-',
            inv.product?.skuCode || '-',
            inv.variant?.variantCode || '-',
            inv.logicalStock,
            inv.allocatedStock,
            inv.freeStock,
            inv.safetyStock,
            inv.isSyncEnabled ? '有効' : '停止',
          ]);

          csvContent = [
            `# 在庫レポート (${new Date().toLocaleDateString('ja-JP')})`,
            '',
            headers.join(','),
            ...rows.map((row: any[]) => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
          ].join('\n');
          filename = `inventory_${new Date().toISOString().split('T')[0]}.csv`;
          break;
        }

        case 'sync': {
          const response = await apiFetch('/api/inventory/sync-queue?limit=1000');
          const data = await response.json();

          if (!response.ok) throw new Error(data.error);

          const headers = ['バリアントID', 'ステータス', '同期在庫数', '対象店舗数', '試行回数', 'エラーメッセージ', '作成日時'];
          const rows = (data.queue || []).map((item: any) => [
            item.variantId.substring(0, 8) + '...',
            getSyncStatusLabel(item.status),
            item.stockToSync,
            item.targetShopIds?.length || 0,
            item.attempts,
            item.errorMessage || '-',
            new Date(item.createdAt).toLocaleString('ja-JP'),
          ]);

          csvContent = [
            `# 同期レポート (${new Date().toLocaleDateString('ja-JP')})`,
            '',
            headers.join(','),
            ...rows.map((row: any[]) => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
          ].join('\n');
          filename = `sync_${new Date().toISOString().split('T')[0]}.csv`;
          break;
        }
      }

      // ダウンロード実行
      if (format === 'csv') {
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        // PDF生成
        generatePDF(reportId, csvContent, filename.replace('.csv', '.pdf'));
      }

      setSuccess(`${reportTypes.find(r => r.id === reportId)?.name}をダウンロードしました`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'レポートのダウンロードに失敗しました');
    } finally {
      setLoading(null);
    }
  }

  function getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      new: '新規', confirmed: '確認済', printed: '印刷済',
      shipped: '発送済', cancelled: 'キャンセル', returned: '返品',
    };
    return labels[status] || status;
  }

  function getSyncStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: '待機中', processing: '処理中', done: '完了', failed: '失敗',
    };
    return labels[status] || status;
  }

  function generatePDF(reportId: string, csvContent: string, filename: string) {
    const doc = new jsPDF();
    const reportType = reportTypes.find(r => r.id === reportId);
    
    // タイトル
    doc.setFontSize(18);
    doc.text(reportType?.name || 'レポート', 14, 20);
    
    // 期間
    doc.setFontSize(10);
    doc.text(`期間: ${startDate} 〜 ${endDate}`, 14, 30);
    doc.text(`出力日時: ${new Date().toLocaleString('ja-JP')}`, 14, 36);
    
    // CSVをテーブルに変換
    const lines = csvContent.split('\n').filter(line => line.trim() && !line.startsWith('#'));
    if (lines.length > 0) {
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const rows = lines.slice(1).map(line => 
        line.split(',').map(cell => cell.replace(/"/g, '').trim())
      );
      
      // @ts-ignore - jspdf-autotableの型定義
      doc.autoTable({
        head: [headers],
        body: rows,
        startY: 45,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
      });
    }
    
    // フッター
    // @ts-ignore - jsPDFのバージョン差異
    const pageCount = doc.internal.pages ? doc.internal.pages.length - 1 : 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    }
    
    doc.save(filename);
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <FileText size={28} />
          レポート
        </h1>
        <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
          各種レポートの出力・ダウンロード
        </p>
      </div>

      {/* メッセージ */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto">×</button>
        </div>
      )}

      {/* 期間選択 */}
      <div className="card p-4">
        <h2 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          <Calendar size={18} className="inline mr-2" />
          レポート期間
        </h2>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border rounded-lg"
              style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
            <span style={{ color: 'var(--text-secondary)' }}>〜</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border rounded-lg"
              style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const d = new Date();
                setStartDate(d.toISOString().split('T')[0]);
                setEndDate(d.toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              今日
            </button>
            <button
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() - 7);
                setStartDate(d.toISOString().split('T')[0]);
                setEndDate(new Date().toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              過去7日
            </button>
            <button
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() - 30);
                setStartDate(d.toISOString().split('T')[0]);
                setEndDate(new Date().toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              過去30日
            </button>
            <button
              onClick={() => {
                const d = new Date();
                setStartDate(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]);
                setEndDate(d.toISOString().split('T')[0]);
              }}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              今月
            </button>
          </div>
        </div>
      </div>

      {/* レポート種類 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isLoadingCSV = loading === `${report.id}-csv`;

          return (
            <div key={report.id} className="card p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${report.color}`}>
                  <Icon size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{report.name}</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{report.description}</p>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => downloadReport(report.id, 'csv')}
                      disabled={!!loading}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 disabled:opacity-50"
                    >
                      {isLoadingCSV ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      CSV
                    </button>
                    <button
                      onClick={() => downloadReport(report.id, 'pdf')}
                      disabled={!!loading}
                      className="px-3 py-1.5 text-sm border rounded-lg flex items-center gap-1 hover:bg-gray-50 transition-colors disabled:opacity-50"
                      style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                    >
                      {loading === `${report.id}-pdf` ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Download size={14} />
                      )}
                      PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 注意書き */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          ※ 受注・売上レポートは選択した期間のデータを出力します。在庫・同期レポートは現在の状態を出力します。
        </p>
      </div>
    </div>
  );
}
